import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { Plus, Search, LayoutGrid, List, ArrowRight, Trash2 } from "lucide-react";

const FALLBACK_STAGES = ["new", "contacted", "qualified", "proposal", "won", "lost"];

export default function Leads() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("table");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", lead_source: "", status: "new" });
  const [stages, setStages] = useState(FALLBACK_STAGES);
  const [sources, setSources] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/crm/leads`, { params: { q: q || undefined, limit: 200 } });
    setItems(data.items || []);
  };
  useEffect(() => {
    load();
    // load config-driven dropdowns (falls back gracefully if empty)
    api.get("/crm/config/pipeline_stages").then((r) => {
      const names = (r.data.items || []).map((x) => x.name);
      if (names.length) setStages(names);
    }).catch(() => {});
    api.get("/crm/config/lead_sources").then((r) => setSources((r.data.items || []).map((x) => x.name))).catch(() => {});
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/crm/leads", form);
      setForm({ name: "", mobile: "", email: "", lead_source: "", status: "new" });
      setShowNew(false); load();
      toast.success("Lead added");
    } catch (e) { toast.error(formatApiError(e, "Failed")); }
  };

  const convert = async (id) => {
    if (!window.confirm("Convert this lead to a client?")) return;
    try {
      await api.post(`/crm/leads/${id}/convert`);
      toast.success("Converted"); load();
    } catch (e) { toast.error(formatApiError(e, "Failed")); }
  };

  const setStage = async (id, status) => {
    await api.patch(`/crm/leads/${id}`, { status });
    load();
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/crm/leads/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e, "Failed")); }
  };

  return (
    <div className="p-6" data-testid="leads-page">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0B0D17]">Leads</h1>
          <p className="text-sm text-[#4A4D57]">Capture, qualify and convert prospects.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg bg-[#F5F5F7] p-1">
            <button onClick={() => setView("table")} className={"px-2 py-1.5 rounded-md " + (view === "table" ? "bg-white shadow-sm" : "")} data-testid="view-table"><List className="w-4 h-4" /></button>
            <button onClick={() => setView("kanban")} className={"px-2 py-1.5 rounded-md " + (view === "kanban" ? "bg-white shadow-sm" : "")} data-testid="view-kanban"><LayoutGrid className="w-4 h-4" /></button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#747680]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads" className="h-9 pl-8 pr-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="leads-search" />
          </div>
          <button onClick={() => setShowNew(true)} className="vp-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="new-lead-btn">
            <Plus className="w-4 h-4" /> New lead
          </button>
        </div>
      </div>

      {view === "table" && (
        <div className="rounded-xl border border-[#E4E5E9] bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-[#F5F5F7] text-[#4A4D57] uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left p-3">Name</th><th className="text-left p-3">Contact</th>
                <th className="text-left p-3">Source</th><th className="text-left p-3">Status</th>
                <th className="text-left p-3">Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-[#E4E5E9] hover:bg-[#F5F5F7]" data-testid={`lead-row-${l.id}`}>
                  <td className="p-3 font-semibold text-[#0B0D17]">{l.name}</td>
                  <td className="p-3 text-[#4A4D57]">{l.mobile || l.email || "—"}</td>
                  <td className="p-3">{l.lead_source || "—"}</td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[#0B5CFF]/10 text-[#0B5CFF] text-xs font-semibold">{l.status}</span>
                    {l.conversion_status === "converted" && <span className="ml-1 inline-block px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-xs font-semibold">Client</span>}
                  </td>
                  <td className="p-3 text-xs text-[#747680]">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <select value={l.status} onChange={(e) => setStage(l.id, e.target.value)} className="text-[11px] border border-[#E4E5E9] rounded px-1 py-0.5 mr-2" data-testid={`lead-status-${l.id}`}>
                      {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {l.conversion_status !== "converted" && (
                      <button onClick={() => convert(l.id)} className="text-xs text-[#0B5CFF] hover:underline inline-flex items-center gap-1 mr-2" data-testid={`convert-${l.id}`}>
                        Convert <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                    <button onClick={() => removeLead(l.id)} className="text-red-500 hover:bg-red-50 rounded p-1" data-testid={`delete-lead-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#747680]">No leads yet. Click New lead to get started.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "kanban" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
          {stages.map((st) => (
            <div key={st} className="min-w-[180px] rounded-xl bg-[#F5F5F7] p-3" data-testid={`kanban-col-${st}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#4A4D57] pb-2 flex items-center justify-between">
                <span>{st}</span>
                <span className="text-[#747680]">{items.filter((x) => x.status === st).length}</span>
              </div>
              {items.filter((x) => x.status === st).map((l) => (
                <div key={l.id} className="bg-white rounded-lg p-2.5 mb-2 border border-[#E4E5E9] shadow-sm">
                  <div className="text-sm font-semibold text-[#0B0D17]">{l.name}</div>
                  <div className="text-xs text-[#747680] truncate">{l.mobile || l.email || "—"}</div>
                  <div className="flex items-center gap-1 mt-2">
                    <select value={l.status} onChange={(e) => setStage(l.id, e.target.value)} className="text-[10px] flex-1 border border-[#E4E5E9] rounded px-1 py-0.5">
                      {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {l.conversion_status !== "converted" && (
                      <button onClick={() => convert(l.id)} className="text-[10px] text-[#0B5CFF]" title="Convert to client">→</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="font-display font-black text-xl mb-4">New lead</h2>
            <div className="space-y-3">
              {[{ k: "name", ph: "Full name *" }, { k: "mobile", ph: "Mobile" }, { k: "email", ph: "Email" }].map((f) => (
                <input key={f.k} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.ph} className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid={`new-lead-${f.k}`} />
              ))}
              {sources.length > 0 ? (
                <select value={form.lead_source} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="new-lead-lead_source">
                  <option value="">Source…</option>
                  {sources.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input value={form.lead_source || ""} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} placeholder="Source (Website, Referral…)" className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="new-lead-lead_source" />
              )}
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm">
                {stages.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={create} className="vp-btn-primary text-sm" data-testid="new-lead-save">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
